import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ExclamationTriangleIcon, ChartBarIcon, DocumentTextIcon, ShieldCheckIcon, PlusIcon, EyeIcon, PencilIcon, UserIcon, CalendarIcon, CheckCircleIcon, ChartPieIcon, MapIcon, Squares2X2Icon, MagnifyingGlassIcon, ScaleIcon, DocumentChartBarIcon, SparklesIcon, ChevronLeftIcon, ChevronRightIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { getCustomerRiskApi, getRiskDataApi, reportRiskDownloadApi } from '../../services/axios/AuthApis/RisksManagementApis';

import RiskManagementDetails from './RiskManagementDetails';
import { get } from 'aws-amplify/api';
import { BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import { RiskDashboard } from './Dashboard/RiskDashboard';
import { useSelector } from 'react-redux';
import { RiskAnalysis } from './RiskAnalysis';
import { RiskEvaluation } from './RiskEvaluation';
import { RiskTreatment } from './RiskTreatment';
import { Reports } from './Report';
import { AIInsights } from './AIInsights';
import RiskManagementList from './RiskList/RiskManagementList';
import CustomerRiskApprovals from './CustomerRiskApprovals';
import TabNavigation from '../ui/TabNavigation';
import PageLoader from '../ui/PageLoader';
import ApiLoader from '../ui/ApiLoader';
import { useSnackbar } from '../../context/SnackbarContext';

const RiskManagement = () => {
  const userDetail = useSelector((state) => state.user);
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [risks, setRisks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [textColumnFilters, setTextColumnFilters] = useState({});
  const [totalRiskCount, setTotalRiskCount] = useState(0);
  const [showingCount, setShowingCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [riskSorting, setRiskSorting] = useState([]);
  // Typing in a header filter can fire several getRisk() calls in close
  // succession; an older, slower request resolving AFTER a newer one
  // would overwrite the correctly filtered result with stale data.
  const riskRequestIdRef = useRef(0);
  const userRole = userDetail?.user?.role || '';
  const isPlatformAdmin = userRole === 'admin' || userRole === 'platform_admin';
  const userId = userDetail?.user?.id || '';
  const roleTabs = {
    admin: [
      { id: 'register', name: 'Risk Register', icon: ExclamationTriangleIcon },
      { id: 'customer-risk-approvals', name: 'Customer Risk Approvals', icon: ClipboardDocumentCheckIcon },
    ],
    customer_admin: [
      { id: 'risk-dashboard', name: 'Dashboard', icon: Squares2X2Icon },
      { id: 'register', name: 'Risk Register', icon: ExclamationTriangleIcon },
      { id: 'risk-analysis', name: 'Risk Analysis', icon: MagnifyingGlassIcon },
      { id: 'risk-evaluation', name: 'Risk Evaluation', icon: ScaleIcon },
      { id: 'treatment', name: 'Risk Treatment Plan', icon: ShieldCheckIcon },
      { id: 'reports', name: 'Reports', icon: DocumentChartBarIcon },
      { id: 'ai-insights', name: 'AI Insights', icon: SparklesIcon }
    ],
    security_analyst: [
      { id: 'risk-dashboard', name: 'Dashboard', icon: Squares2X2Icon },
      { id: 'register', name: 'Risk Register', icon: ExclamationTriangleIcon },
      { id: 'risk-analysis', name: 'Risk Analysis', icon: MagnifyingGlassIcon },
      { id: 'risk-evaluation', name: 'Risk Evaluation', icon: ScaleIcon },
      { id: 'treatment', name: 'Risk Treatment Plan', icon: ShieldCheckIcon },
      { id: 'reports', name: 'Reports', icon: DocumentChartBarIcon },
      { id: 'ai-insights', name: 'AI Insights', icon: SparklesIcon }
    ],
    end_user: [
      { id: 'register', name: 'Risk Register', icon: ExclamationTriangleIcon },
      { id: 'risk-analysis', name: 'Risk Analysis', icon: MagnifyingGlassIcon },
      { id: 'risk-evaluation', name: 'Risk Evaluation', icon: ScaleIcon },
      { id: 'treatment', name: 'Risk Treatment Plan', icon: ShieldCheckIcon },
      { id: 'reports', name: 'Reports', icon: DocumentChartBarIcon },
      { id: 'ai-insights', name: 'AI Insights', icon: SparklesIcon }
    ],
    auditor: [
      { id: 'risk-dashboard', name: 'Dashboard', icon: Squares2X2Icon },
      { id: 'register', name: 'Risk Register', icon: ExclamationTriangleIcon },
      { id: 'risk-analysis', name: 'Risk Analysis', icon: MagnifyingGlassIcon },
      { id: 'risk-evaluation', name: 'Risk Evaluation', icon: ScaleIcon },
      { id: 'treatment', name: 'Risk Treatment Plan', icon: ShieldCheckIcon },
      { id: 'reports', name: 'Reports', icon: DocumentChartBarIcon },
      { id: 'ai-insights', name: 'AI Insights', icon: SparklesIcon }
    ]
  };
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = isPlatformAdmin || userRole == "end_user" ? useState(searchParams.get('tab') || 'register') : useState(searchParams.get('tab') || 'risk-dashboard');

  // Lets a notification deep-link into a specific tab via ?tab=register,
  // same convention CSPM already uses.
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedRisk, setSelectedRisk] = useState();
  const selectedCompany = useSelector((state) => state.company.selectedCompany);
  const getRisk = async (page = 1) => {
    const requestId = ++riskRequestIdRef.current;
    setLoading(true);
    try {
      let response;
      const sort = riskSorting[0] ? (riskSorting[0].desc ? 'desc' : 'asc') : 'desc';
      const sortedColumnId = riskSorting[0]?.id;
      const columnSortFields = {
        framework: 'framework_control',
        impact: 'inherent_impact_type',
        likelihood: 'inherent_likelihood_type',
        risk_score: 'inherent_risk_score',
      };
      const sortBy = sortedColumnId
        ? columnSortFields[sortedColumnId] || sortedColumnId
        : 'created_at';
      const adminSortBy = isPlatformAdmin && sortBy === 'risk_status'
        ? 'status'
        : sortBy;
      if (isPlatformAdmin)
        response = await getRiskDataApi(
          searchTerm,
          filterStatus,
          filterSeverity,
          page,
          itemsPerPage,
          sort,
          adminSortBy,
          Object.entries(textColumnFilters).map(([id, value]) => ({ id, value }))
        );
      else if (userRole == "customer_admin" || userRole == "end_user")
        response = await getCustomerRiskApi(
          searchTerm,
          filterStatus,
          filterSeverity,
          page,
          itemsPerPage,
          userDetail.user.id,
          sort,
          sortBy,
          Object.entries(textColumnFilters).map(([id, value]) => ({ id, value }))
        );
      else if ((userRole == "security_analyst" || userRole == "auditor") && selectedCompany) {
        response = await getCustomerRiskApi(
          searchTerm,
          filterStatus,
          filterSeverity,
          page,
          itemsPerPage,
          selectedCompany.user_id,
          sort,
          sortBy,
          Object.entries(textColumnFilters).map(([id, value]) => ({ id, value }))
        );
      }
      if (requestId !== riskRequestIdRef.current) return;
      if (response.success) {
        setRisks(response.data || []);
        setTotalRiskCount(response.total_count || 0);
        setShowingCount(response.returned_count || 0);
        setCurrentPage(page || 1);
      }
    } catch (error) {
      console.error('Error fetching risk data:', error);
    } finally {
      if (requestId === riskRequestIdRef.current) {
        setLoading(false);
        setInitialLoading(false);
      }
    }
  };
  const getRiskList = (page = 1) => {
    const safePage = Number.isFinite(page) && page > 0 ? page : 1;
    setCurrentPage(safePage);
    getRisk(safePage);
  };
  useEffect(() => {
    getRiskList(1);
  }, [
    filterStatus,
    filterSeverity,
    itemsPerPage,
    riskSorting,
    searchTerm,
    textColumnFilters,
    userRole,
    userId,
    selectedCompany?.user_id,
  ]);
  const handleExport = async (format) => {
    try {
      const reportId = "risk-register";
      const customerId = ((userRole == "security_analyst" || userRole == "auditor") && selectedCompany) ? selectedCompany?.user_id : userId;
      const { blob, filename } = await reportRiskDownloadApi(
        reportId,
        customerId,
        "download",
        userRole,
        format
      );

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);

      showSnackbar(`${format.toUpperCase()} risk register exported successfully.`, 'success');
    } catch (error) {
      console.error(`Error exporting data in ${format} format:`, error);
      showSnackbar(error?.response?.data?.message || 'Failed to export risk register.', 'error');
    }
  };
  // Badge colours for a risk level (used by the Risk Assessment tab).
  const getRiskColor = (level) => {
    switch (level) {
      case 'Critical': return 'text-red-700 bg-red-100 border-red-200';
      case 'High': return 'text-red-600 bg-red-50 border-red-200';
      case 'Medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'Low': return 'text-green-600 bg-green-50 border-green-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  //Risk Dashboard Tab
  const RiskDashboardTab = () => (
    <>
      {loading ? (
        <ApiLoader label="Loading Dashboard…" className="py-20" />
      ) : (
        <RiskDashboard userId={userDetail.user.id} userRole={userRole} />
      )}
    </>
  );

  // Risk Register Tab
  const RiskRegisterTab = () => (
    <RiskManagementList
      risks={risks}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      filterStatus={filterStatus}
      setFilterStatus={setFilterStatus}
      filterSeverity={filterSeverity}
      setFilterSeverity={setFilterSeverity}
      textColumnFilters={textColumnFilters}
      setTextColumnFilters={setTextColumnFilters}
      getRiskList={getRiskList}
      sorting={riskSorting}
      onSortingChange={setRiskSorting}
      totalRiskCount={totalRiskCount}
      showingCount={showingCount}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      itemsPerPage={itemsPerPage}
      setItemsPerPage={setItemsPerPage}
      userRole={userRole}
      onExport={handleExport}
      RiskDataloading={loading}
    />
  );

  // Risk Details Tab
  const RiskDetailsTab = () => (
    <RiskManagementDetails risks={risks}
    />
  );

  const RiskAnalysisTab = () => <RiskAnalysis />;
  const RiskEvaluationTab = () => <RiskEvaluation />;
  // Risk Treatment Plan Tab
  const RiskTreatmentTab = () => <RiskTreatment />;
  const ReportsTab = () => (
    <Reports
      userId={userDetail.user.id}
      userRole={userRole}
    />
  );
  const AIInsightsTab = () => (
    <AIInsights
      userId={userDetail.user.id} />
  );

  // Risk Assessment Tab
  const RiskAssessmentTab = () => (
    <div className="space-y-6">
      {/* Risk Scoring */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <ChartBarIcon className="w-6 h-6 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900">Risk Assessment & Scoring</h3>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-red-600 mb-2">8</div>
            <div className="text-sm text-gray-600">High Risk Items</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-yellow-600 mb-2">12</div>
            <div className="text-sm text-gray-600">Medium Risk Items</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600 mb-2">5</div>
            <div className="text-sm text-gray-600">Low Risk Items</div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Inherent Risk Scoring</h4>
            <div className="space-y-3">
              {risks.map((risk) => (
                <div key={risk.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{risk.title}</div>
                    <div className="text-xs text-gray-500">P: {risk.probability} × I: {risk.impact}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">{risk.riskScore}</div>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getRiskColor(risk.inherentRisk)}`}>
                      {risk.inherentRisk}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Residual Risk Scoring</h4>
            <div className="space-y-3">
              {risks.map((risk) => (
                <div key={risk.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{risk.title}</div>
                    <div className="text-xs text-gray-500">After Controls</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-gray-900">{Math.round(risk.riskScore * 0.6)}</div>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getRiskColor(risk.residualRisk)}`}>
                      {risk.residualRisk}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Risk Heatmap Visualization */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3 mb-6">
          <MapIcon className="w-6 h-6 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">Risk Heatmap Visualization</h3>
        </div>

        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <ChartPieIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">Interactive Risk Heatmap</h4>
          <p className="text-gray-600 mb-4">
            Visual representation of risks plotted by probability and impact
          </p>
          <button className="inline-flex items-center px-4 py-2 bg-purple-600 border border-transparent rounded-lg text-sm font-medium text-white hover:bg-purple-700">
            <ChartBarIcon className="w-4 h-4 mr-2" />
            Generate Heatmap
          </button>
        </div>
      </div>
    </div>
  );

  if (initialLoading) {
    return (
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center h-64">
          <PageLoader title="Loading Risk Management..." minH="min-h-screen" />
        </div>
      </div>
    );
  }

  return (
    <div className="py-6 px-4">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {/* Left side - Title */}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Risk Management</h1>
            <p className="text-sm text-gray-600 mt-1">Identify, assess, and mitigate organizational risks</p>
          </div>
        </div>
      </div>

      {/* Tab Content shared across both layouts */}
      {(() => {
        const tabContent = (
          <AnimatePresence mode="wait">
            {activeTab === 'risk-dashboard' && (
              <motion.div key="risk-dashboard" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                <RiskDashboardTab />
              </motion.div>
            )}
            {activeTab === 'register' && (
              <motion.div key="register" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                {RiskRegisterTab()}
              </motion.div>
            )}
            {activeTab === 'customer-risk-approvals' && isPlatformAdmin && (
              <motion.div key="customer-risk-approvals" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                <CustomerRiskApprovals />
              </motion.div>
            )}
            {activeTab === 'risk-analysis' && (
              <motion.div key="risk-analysis" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                <RiskAnalysisTab />
              </motion.div>
            )}
            {activeTab === 'risk-evaluation' && (
              <motion.div key="risk-evaluation" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                <RiskEvaluationTab />
              </motion.div>
            )}
            {activeTab === 'assessment' && (
              <motion.div key="assessment" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                <RiskAssessmentTab />
              </motion.div>
            )}
            {activeTab === 'treatment' && (
              <motion.div key="treatment" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                <RiskTreatmentTab />
              </motion.div>
            )}
            {activeTab === 'reports' && (
              <motion.div key="reports" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                <ReportsTab />
              </motion.div>
            )}
            {activeTab === 'ai-insights' && (
              <motion.div key="ai-insights" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                <AIInsightsTab />
              </motion.div>
            )}
          </AnimatePresence>
        );

        /* ── All roles: segment-control tab bar ── */
        return (
          <>
            <TabNavigation
              tabs={roleTabs[isPlatformAdmin ? 'admin' : userRole] || []}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
            {tabContent}
          </>
        );
      })()}
    </div>
  );
};

export default RiskManagement;